import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, TrendingDown } from 'lucide-react';
import { Button } from './ui/button';

const VitalMethodPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black">
      {/* Header */}
      <div className="bg-black/80 backdrop-blur-md border-b border-white/10">
        <div className="container mx-auto px-4 py-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <img 
              src="/images/vf-logo.png" 
              alt="Vital Fusion Coaching Logo" 
              className="h-10 w-10 object-contain"
            />
            <span className="text-2xl font-bold bg-gradient-to-r from-[#5F9BC7] to-[#FFE7AB] bg-clip-text text-transparent">
              Vital Fusion Coaching
            </span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="container mx-auto px-4 py-16 max-w-5xl">
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            The <span className="bg-gradient-to-r from-[#5F9BC7] to-[#FFE7AB] bg-clip-text text-transparent">VITAL</span> Method
          </h1>
          <p className="text-2xl text-gray-300 mb-8">
            A Proven System for Sustainable Fat Loss
          </p>
          
          {/* 30% Discount Banner */}
          <div className="inline-block bg-gradient-to-r from-[#FFE7AB]/20 to-[#5F9BC7]/20 border-2 border-[#FFE7AB] rounded-2xl px-8 py-4 mb-8">
            <p className="text-[#FFE7AB] font-bold text-xl">
              🎉 Limited Time: 30% OFF 1:1 Coaching
            </p>
            <p className="text-gray-300 text-sm mt-1">
              For the next 5 people that sign up
            </p>
          </div>
        </div>

        {/* VITAL Breakdown */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 md:p-12 mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            What is the VITAL Method?
          </h2>
          
          <div className="space-y-6">
            {/* V */}
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-[#5F9BC7] to-[#4A8AB0] flex items-center justify-center">
                <span className="text-white font-bold text-xl">V</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#FFE7AB] mb-2">Verify the Barriers</h3>
                <p className="text-gray-300">Identify what's actually holding you back from progress</p>
              </div>
            </div>

            {/* I */}
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-[#5F9BC7] to-[#4A8AB0] flex items-center justify-center">
                <span className="text-white font-bold text-xl">I</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#FFE7AB] mb-2">Install Simple Structure</h3>
                <p className="text-gray-300">Create systems that fit YOUR life, not the other way around</p>
              </div>
            </div>

            {/* T */}
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-[#5F9BC7] to-[#4A8AB0] flex items-center justify-center">
                <span className="text-white font-bold text-xl">T</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#FFE7AB] mb-2">Target Sustainable Fat Loss</h3>
                <p className="text-gray-300">Focus on steady progress you can maintain, not crash diets</p>
              </div>
            </div>

            {/* A */}
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-[#5F9BC7] to-[#4A8AB0] flex items-center justify-center">
                <span className="text-white font-bold text-xl">A</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#FFE7AB] mb-2">Audit and Adapt</h3>
                <p className="text-gray-300">Regular check-ins to adjust your plan based on real results</p>
              </div>
            </div>

            {/* L */}
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-[#5F9BC7] to-[#4A8AB0] flex items-center justify-center">
                <span className="text-white font-bold text-xl">L</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#FFE7AB] mb-2">Learn to Maintain Long-Term</h3>
                <p className="text-gray-300">Build habits that last beyond the program</p>
              </div>
            </div>
          </div>
        </div>

        {/* Client Transformation */}
        <div className="bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-sm border border-white/10 rounded-3xl p-8 md:p-12 mb-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-3">
              Real Client Transformation
            </h2>
            <p className="text-gray-300 text-lg">
              Charlie G.'s 12-Week Journey with the VITAL Method
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Progress Graph */}
            <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-[#5F9BC7]/30 rounded-2xl p-4 overflow-hidden">
              <img 
                src="/images/charlie-progress.jpg" 
                alt="Charlie G. Weight Loss Progress - 14lbs in 12 weeks"
                className="w-full h-auto rounded-lg"
              />
            </div>

            {/* Results */}
            <div className="space-y-6">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-white font-semibold text-lg">Lost 14lbs in 12 weeks</p>
                  <p className="text-gray-400 text-sm">Consistent, sustainable progress</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-white font-semibold text-lg">No extreme dieting</p>
                  <p className="text-gray-400 text-sm">Enjoyed the foods he loves</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-white font-semibold text-lg">Balanced work & family</p>
                  <p className="text-gray-400 text-sm">Flexible workouts that fit his schedule</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-white font-semibold text-lg">Visible results</p>
                  <p className="text-gray-400 text-sm">Family noticed the transformation</p>
                </div>
              </div>

              <div className="mt-6 p-4 bg-[#FFE7AB]/10 border border-[#FFE7AB]/30 rounded-xl">
                <p className="text-gray-300 italic text-sm leading-relaxed">
                  "Foods been great 👍, I feel like I still have different options for foods I like. Gym has been fine, I will admit that sometimes I don't hit every machine in the workout because the machine is ALWAYS busy. But I have some home workouts that I try to fill the gaps. Overall, I'm feeling great. I'm even getting comments from my family about my weight loss."
                </p>
                <p className="text-[#FFE7AB] font-semibold mt-2">- Charlie G.</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-r from-[#5F9BC7]/20 to-[#FFE7AB]/20 border-2 border-[#5F9BC7]/50 rounded-3xl p-6 md:p-12">
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            Ready to Start Your Transformation?
          </h2>
          <p className="text-lg md:text-xl text-gray-300 mb-6">
            Apply now and get 30% off
          </p>
          <p className="text-sm md:text-base text-gray-400 mb-8">
            Limited spots available • Personalized coaching
          </p>

          <Button
            onClick={() => navigate('/vital-method-application')}
            className="w-full md:w-auto bg-gradient-to-r from-[#FFE7AB] to-[#E6D199] hover:from-[#E6D199] hover:to-[#FFE7AB] text-black px-8 md:px-12 py-4 md:py-6 text-lg md:text-xl rounded-xl font-bold shadow-2xl shadow-[#FFE7AB]/40 transition-all duration-300 hover:shadow-[#FFE7AB]/60 hover:scale-105"
          >
            Apply Now - Get 30% Off
          </Button>

          <p className="text-xs md:text-sm text-gray-500 mt-4">
            Offer expires soon • Limited availability
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-white/10 py-8">
        <p className="text-center text-gray-500 text-sm">
          © {new Date().getFullYear()} Vital Fusion Coaching. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default VitalMethodPage;
